import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import Icon from '../components/Icon';
import Badge from '../components/Badge';
import Button from '../components/Button';

function HomeDashboard() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showOnboardingReminder, setShowOnboardingReminder] = useState(false);
  const [insightsExpanded, setInsightsExpanded] = useState(true); // Default to expanded

  useEffect(() => {
    // Simple fade-in animation
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    // Load user profile
    const profile = localStorage.getItem('userProfile');
    if (profile) {
      setUserProfile(JSON.parse(profile));
    } else {
      // Check if we should show onboarding reminder
      const reminder = localStorage.getItem('onboardingReminder');
      if (reminder) {
        setShowOnboardingReminder(true);
      }
    }
    
    // Scroll to top
    window.scrollTo(0, 0);
    
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: 'chat',
      title: 'BridgeBot Chat',
      description: 'AI-powered support assistant',
      path: '/chat',
      color: '#4361ee',
      tooltip: 'bridgebot'
    },
    {
      icon: 'quests',
      title: 'Daily Quests',
      description: 'Gamified wellness challenges',
      path: '/quest',
      color: '#f72585',
      tooltip: 'quests'
    },
    {
      icon: 'breathing',
      title: 'Breathing Exercise',
      description: 'Guided mindfulness sessions',
      path: '/biofeedback',
      color: '#4cc9f0',
      tooltip: 'biofeedback'
    },
    {
      icon: 'nutrition',
      title: 'Food Scanner',
      description: 'Track nutrition with AI',
      path: '/nutrition',
      color: '#4ade80',
      tooltip: 'nutrition'
    },
    {
      icon: 'sleep',
      title: 'Sleep Tracker',
      description: 'Monitor sleep patterns',
      path: '/sleep',
      color: '#7209b7',
      tooltip: 'sleep'
    },
    {
      icon: 'social',
      title: 'Social Browser',
      description: 'Connect with community',
      path: '/social',
      color: '#e91e63',
      tooltip: 'social'
    },
    {
      icon: 'focus',
      title: 'Focus Bubble',
      description: 'Enhance concentration',
      path: '/focus',
      color: '#00bcd4',
      tooltip: 'focus'
    },
    {
      icon: 'peer',
      title: 'Peer Support',
      description: 'Connect with peers',
      path: '/peer',
      color: '#ff5722',
      tooltip: 'peer'
    }
  ];

  // Create a wrapper function to ensure navigation works
  const handleFeatureClick = (path) => {
    console.log('Navigating to:', path);
    navigate(path);
  };

  // Get personalized insights based on user profile
  const getPersonalizedInsights = () => {
    if (!userProfile || userProfile.skipped) return null;
    
    const insights = [];
    
    // Risk level insight
    if (userProfile.riskLevel === "low") {
      insights.push({
        title: "Great Foundation",
        description: "You're building strong wellness habits. Keep up the positive momentum!",
        color: "success"
      });
    } else if (userProfile.riskLevel === "moderate") {
      insights.push({
        title: "Room for Growth",
        description: "Small daily improvements can make a big difference in your wellness journey.",
        color: "warning"
      });
    } else {
      insights.push({
        title: "Extra Support",
        description: "You're taking a brave step by seeking support. We're here for you.",
        color: "primary"
      });
    }
    
    // Lifestyle insights
    if (userProfile.lifestyle) {
      if (userProfile.lifestyle.sleep === "3" || userProfile.lifestyle.sleep === "2") {
        insights.push({
          title: "Sleep Focus",
          description: "Improving your sleep quality can boost your mood and energy.",
          color: "info"
        });
      }
      
      if (userProfile.lifestyle.social === "3" || userProfile.lifestyle.social === "2") {
        insights.push({
          title: "Social Connection",
          description: "Connecting with others can provide valuable support and reduce stress.",
          color: "secondary"
        });
      }
      
      if (userProfile.lifestyle.exercise === "3" || userProfile.lifestyle.exercise === "2") {
        insights.push({
          title: "Movement Matters",
          description: "Even light physical activity can improve your mental wellbeing.",
          color: "primary"
        });
      }
    }
    
    return insights;
  };

  const insights = getPersonalizedInsights();

  // Handle completing profile
  const handleCompleteProfile = () => {
    localStorage.removeItem('onboardingReminder');
    setShowOnboardingReminder(false);
    navigate('/onboarding');
  };

  // Handle dismissing reminder
  const handleDismissReminder = () => {
    localStorage.removeItem('onboardingReminder');
    setShowOnboardingReminder(false);
  };

  // Toggle insights section
  const toggleInsights = () => {
    setInsightsExpanded(!insightsExpanded);
  };

  return (
    <div style={{ 
      maxWidth: '480px', 
      margin: 'auto', 
      padding: '20px', 
      backgroundColor: '#f5f7fa', 
      minHeight: '100vh',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
    }}>
      
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <h1 style={{ 
          color: '#2c3e50', 
          fontSize: '28px', 
          marginBottom: '8px',
          fontWeight: '700'
        }}>
          🌉 MindBridge
        </h1>
        <p style={{ 
          color: '#64748b', 
          fontSize: '16px', 
          margin: 0 
        }}>
          Your mental wellness companion
        </p>
      </div>
      
      {/* Onboarding Reminder */}
      {showOnboardingReminder && (
        <Card style={{ 
          marginBottom: '20px', 
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ 
                color: '#92400e', 
                marginBottom: '8px',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                💡 Complete Your Profile
              </h3>
              <p style={{ 
                color: '#92400e', 
                fontSize: '14px',
                margin: '0 0 12px 0'
              }}>
                Get personalized insights and recommendations by completing your wellness profile.
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button 
                  onClick={handleCompleteProfile}
                  size="sm"
                  style={{ 
                    padding: '6px 12px',
                    fontSize: '12px',
                    backgroundColor: '#f59e0b'
                  }}
                >
                  Complete Now
                </Button>
                <Button 
                  onClick={handleDismissReminder}
                  variant="outline"
                  size="sm"
                  style={{ 
                    padding: '6px 12px',
                    fontSize: '12px',
                    borderColor: '#d1d5db',
                    color: '#6b7280'
                  }}
                >
                  Dismiss
                </Button>
              </div>
            </div>
            <div style={{ fontSize: '24px', color: '#f59e0b' }}>🔔</div>
          </div>
        </Card>
      )}
      
      {/* Personalized Insights - Collapsible Section */}
      {insights && insights.length > 0 && (
        <Card style={{ marginBottom: '20px', backgroundColor: '#f0f9ff' }}>
          <div 
            onClick={toggleInsights}
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              cursor: 'pointer',
              padding: '10px 0',
              borderBottom: insightsExpanded ? '1px solid #e2e8f0' : 'none',
              marginBottom: insightsExpanded ? '12px' : '0'
            }}
          >
            <h3 style={{ 
              color: '#2c3e50', 
              margin: 0,
              fontSize: '18px',
              fontWeight: '600'
            }}>
              Your Wellness Insights
            </h3>
            <div style={{ 
              fontSize: '18px', 
              transition: 'transform 0.3s ease',
              transform: insightsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}>
              ▼
            </div>
          </div>
          
          {insightsExpanded && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {insights.map((insight, index) => (
                <Badge 
                  key={index} 
                  variant={insight.color}
                  style={{ 
                    display: 'block',
                    textAlign: 'left',
                    padding: '8px 12px'
                  }}
                >
                  <div style={{ fontWeight: '600', fontSize: '12px' }}>{insight.title}</div>
                  <div style={{ fontSize: '11px', opacity: 0.9 }}>{insight.description}</div>
                </Badge>
              ))}
            </div>
          )}
        </Card>
      )}
      
      {/* Recommendations */}
      {userProfile && userProfile.recommendations && !userProfile.skipped && (
        <Card style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            color: '#2c3e50', 
            marginBottom: '12px',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            Recommended for You
          </h3>
          <ul style={{ 
            margin: 0, 
            padding: '0 0 0 20px',
            color: '#64748b'
          }}>
            {userProfile.recommendations.slice(0, 3).map((rec, index) => (
              <li key={index} style={{ marginBottom: '8px', fontSize: '14px' }}>
                {rec}
              </li>
            ))}
          </ul>
        </Card>
      )}
      
      {/* Quick Stats */}
      {userProfile && !userProfile.skipped && (
        <Card style={{ marginBottom: '20px', backgroundColor: '#f8fafc' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#4361ee' }}>
                {userProfile.riskLevel === "low" ? "😊" : 
                 userProfile.riskLevel === "moderate" ? "😐" : "😢"}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Mood</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#4ade80' }}>
                {userProfile.lifestyle ? "3/7" : "N/A"}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Goals</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#f72585' }}>
                2
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Days Streak</div>
            </div>
          </div>
        </Card>
      )}

      {/* Feature Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(2, 1fr)', 
        gap: '16px',
        marginBottom: '20px'
      }}>
        {features.map((feature, index) => (
          <div
            key={index}
            onClick={() => handleFeatureClick(feature.path)}
            style={{
              cursor: 'pointer',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
              transition: `all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 0.05}s`
            }}
          >
            <Card 
              style={{ 
                textAlign: 'center',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s ease-in-out'
              }}
              hoverable
            >
              <div style={{ 
                width: '48px',
                height: '48px',
                backgroundColor: `${feature.color}20`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px auto'
              }}>
                <Icon 
                  name={feature.icon} 
                  size={24} 
                  color={feature.color}
                />
              </div>
              
              <h3 style={{ 
                color: feature.color, 
                margin: '0 0 4px 0', 
                fontSize: '16px',
                fontWeight: '600'
              }}>
                {feature.title}
              </h3>
              
              <p style={{ 
                color: '#64748b', 
                margin: 0, 
                fontSize: '12px' 
              }}>
                {feature.description}
              </p>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}

export default HomeDashboard;
